import React from 'react';
import { useOrganizationProfile, formatOrgAddressLines } from '../hooks/useOrganizationProfile';
import { displayWebAddress } from '../utils/webAddress';

/**
 * Top-left org logo + top-right org name for print views (Org Admin → Org Settings).
 */
export function OrgPrintHeader({ organization: orgProp }) {
  const orgFromHook = useOrganizationProfile();
  const org = orgProp ?? orgFromHook;
  if (!org) return null;

  const hasLogo = Boolean(org.logoUrl && String(org.logoUrl).trim());
  const hasName = Boolean(org.name && String(org.name).trim());
  if (!hasLogo && !hasName) return null;

  return (
    <div className="org-print-header">
      <div className="org-print-header-row">
        <div className="org-print-header-left">
          {hasLogo && (
            <img
              src={org.logoUrl}
              alt={org.name || 'Organization logo'}
              className="org-print-logo"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
        </div>
        <div className="org-print-header-right">
          {hasName && <div className="org-print-org-name">{org.name}</div>}
        </div>
      </div>
      <hr className="org-print-header-rule" />
    </div>
  );
}

/**
 * Bottom org contact block for print views.
 */
export function OrgPrintFooter({ organization: orgProp }) {
  const orgFromHook = useOrganizationProfile();
  const org = orgProp ?? orgFromHook;
  if (!org) return null;

  const addressLines = formatOrgAddressLines(org);
  const contacts = [
    org.phoneNumber ? `Phone: ${org.phoneNumber}` : null,
    org.email ? `Email: ${org.email}` : null,
    org.website ? `Web: ${displayWebAddress(org.website)}` : null
  ].filter(Boolean);

  if (!org.name && addressLines.length === 0 && contacts.length === 0) {
    return null;
  }

  return (
    <div className="org-print-footer">
      <hr className="org-print-footer-rule" />
      <div className="org-print-footer-grid">
        <div className="org-print-footer-left">
          {org.name && <div className="org-print-footer-name">{org.name}</div>}
          {addressLines.map((line) => (
            <div key={line} className="org-print-footer-line">{line}</div>
          ))}
        </div>
        <div className="org-print-footer-right">
          {contacts.map((line) => (
            <div key={line} className="org-print-footer-line">{line}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Wraps print body content with org logo header and contact footer.
 */
export default function OrgPrintChrome({ children, organization }) {
  return (
    <>
      <OrgPrintHeader organization={organization} />
      {children}
      <OrgPrintFooter organization={organization} />
    </>
  );
}
